import { useEffect, useRef, useState } from "react";

/**
 * Single reusable carousel used for the logo strip, movie/series posters,
 * the sports cards, and the hero's vertical poster columns.
 *
 * Pure CSS-driven infinite marquee: a duplicated track animates continuously
 * via `transform` (GPU-friendly, 60fps-capable) and is NEVER paused by
 * hover/click/touch/focus — there is no JS play/pause logic wired to any
 * interaction, by design, so the motion can't be interrupted. Clicks on
 * cards inside the track work completely normally since the animation lives
 * on the track element, not on the individual items.
 */
export default function Carousel({
  items,
  renderItem,
  keyExtractor,
  speed = 0.55, // legacy "px per animation frame @60fps" unit — kept so existing call sites don't need retuning
  direction = "normal",
  axis = "x",
  gapClassName = "gap-4 sm:gap-5",
  className = "",
  edgeFade = true,
}) {
  const trackRef = useRef(null);
  const [duration, setDuration] = useState(30);
  const isVertical = axis === "y";
  const isReverse = direction === "reverse";
  const pxPerSecond = Math.max(speed, 0.05) * 60;

  const loopItems = [...items, ...items];

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    const measure = () => {
      const total = isVertical ? el.scrollHeight : el.scrollWidth;
      const half = total / 2;
      if (half > 0) {
        setDuration(half / pxPerSecond);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isVertical, pxPerSecond, items.length]);

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Alpha-mask fade instead of a solid-color overlay: it reveals whatever is
  // actually behind the carousel (textured hero glow, flat section color,
  // anything) with zero risk of a mismatched seam at the edges.
  const maskStyle = edgeFade
    ? {
        WebkitMaskImage: isVertical
          ? "linear-gradient(to bottom, transparent 0, black 28px, black calc(100% - 28px), transparent 100%)"
          : "linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
        maskImage: isVertical
          ? "linear-gradient(to bottom, transparent 0, black 28px, black calc(100% - 28px), transparent 100%)"
          : "linear-gradient(to right, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
      }
    : undefined;

  return (
    <div
      className="relative h-full min-h-0 overflow-hidden"
      style={maskStyle}
    >
      <div
        ref={trackRef}
        className={`flex ${isVertical ? "flex-col h-max w-full" : "w-max"} ${gapClassName} carousel-track ${className}`}
        style={
          prefersReduced
            ? undefined
            : {
                animationName: isVertical
                  ? "carousel-scroll-y"
                  : "carousel-scroll-x",
                animationDuration: `${duration}s`,
                animationTimingFunction: "linear",
                animationIterationCount: "infinite",
                animationDirection: isReverse ? "reverse" : "normal",
              }
        }
      >
        {loopItems.map((item, index) => (
          <div
            key={
              keyExtractor
                ? `${keyExtractor(item, index)}-${index}`
                : index
            }
            className="flex-shrink-0"
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
}
